import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTracking } from "../../lib/analyticsEvents";
import { isFriendlyUI } from "../../themes/forumTheme";
import classNames from "classnames";
import DeferRender from "../common/DeferRender";
import CommentsNode from "../comments/CommentsNode";
import LWQuickTakesCollapsedListItem from "./LWQuickTakesCollapsedListItem";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';
import { NoSideItems } from '../contents/SideItems';

const styles = defineStyles("QuickTakesListItem", (theme: ThemeType) => ({
  root: {
    // Include the expanded comment's margins in the animated height.
    display: "flow-root",
  },
  expandedRoot: {
    position: "relative",
    "& .comments-node-root": {
      marginBottom: 8,
      paddingTop: 0,
      // This is to cause the "scroll to parent" sidebar to be positioned with respect to the top-level comment node, rather than the entire section
      position: 'relative',

    },
  },
  hidden: {
    display: 'none',
  },
}));

const QuickTakesListItem = ({quickTake, linesToDisplay=2}: {
  quickTake: FrontpageShortformComments,
  linesToDisplay?: number,
}) => {
  const classes = useStyles(styles);
  const {captureEvent} = useTracking();
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const heightBeforeExpansionRef = useRef<number|null>(null);

  useLayoutEffect(() => {
    const element = rootRef.current;
    const previousHeight = heightBeforeExpansionRef.current;
    heightBeforeExpansionRef.current = null;
    if (!expanded || !element || previousHeight === null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const expandedHeight = element.getBoundingClientRect().height;
    if (expandedHeight <= previousHeight) {
      return;
    }

    const animation = element.animate([
      { height: `${previousHeight}px`, overflow: 'clip' },
      { height: `${expandedHeight}px`, overflow: 'clip' },
    ], { duration: 200, easing: 'ease-out' });

    return () => animation.cancel();
  }, [expanded]);

  const wrappedSetExpanded = useCallback((value: boolean) => {
    if (value && !expanded) {
      heightBeforeExpansionRef.current = rootRef.current?.getBoundingClientRect().height ?? null;
    }
    setExpanded(value);
    captureEvent(value ? "shortformItemExpanded" : "shortformItemCollapsed");
  }, [captureEvent, expanded]);

  // We're doing both a NoSSR + conditional `display: 'none'` to toggle between the collapsed & expanded quick take
  // This is to eliminate a loading spinner (for the child comments) when someone expands a quick take,
  // while avoiding the impact to the home page SSR speed for the large % of users who won't interact with quick takes at all
  const expandedComment = (
    <DeferRender ssr={false}>
      <div className={classNames(classes.expandedRoot, { [classes.hidden]: !expanded })}>
        <CommentsNode
          treeOptions={{
            post: quickTake.post ?? undefined,
            showCollapseButtons: isFriendlyUI(),
            onToggleCollapsed: () => wrappedSetExpanded(!expanded),
          }}
          comment={quickTake}
          loadChildrenSeparately
          forceUnTruncated
          forceUnCollapsed
        />
      </div>
    </DeferRender>
  );

  const collapsedComment = (
    <div className={classNames({ [classes.hidden]: expanded })}>
      <LWQuickTakesCollapsedListItem quickTake={quickTake} setExpanded={wrappedSetExpanded} linesToDisplay={linesToDisplay} />
    </div>
  );

  return <div ref={rootRef} className={classes.root}>
    <NoSideItems when={!expanded}>
      {expandedComment}
    </NoSideItems>
    <NoSideItems>
      {collapsedComment}
    </NoSideItems>
  </div>;
}

export default QuickTakesListItem;
